import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill, Empty, inputCls } from "@/components/kit";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/lib/seo";
import { FormDialog, SectionHead, Toggle } from "@/pages/manage/common";

const BLANK = { title: "", slug: "", body: "", seo_title: "", meta_description: "", show_in_footer: true, published: true };

export default function Pages() {
  const [pages, setPages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(BLANK);
  const { reload } = useSite();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const load = () => api.get("/platform/pages").then((r) => setPages(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/platform/pages/${editing.id}`, f);
      else await api.post("/platform/pages", f);
      toast.success("Page saved");
      setOpen(false);
      load();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.title}?`)) return;
    await api.delete(`/platform/pages/${p.id}`);
    load();
    reload();
  };

  return (
    <div className="space-y-6">
      <SectionHead title="Pages" text="About, Terms, Privacy, Contact — any page you need, linked from the footer.">
        <Button className="rounded-full font-bold" onClick={() => { setEditing(null); setF(BLANK); setOpen(true); }} data-testid="add-page-btn"><Plus className="mr-1 h-4 w-4" /> New page</Button>
      </SectionHead>
      {!pages.length && <Empty icon={FileText} title="No pages yet" text="Create your first page, e.g. Privacy Policy." />}
      <Panel className="divide-y divide-white/5">
        {pages.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`admin-page-${p.slug}`}>
            <div><p className="font-semibold text-white">{p.title}</p><p className="font-mono text-xs text-slate-500">/p/{p.slug}</p></div>
            <div className="flex items-center gap-2">
              <Pill tone={p.published ? "green" : "amber"}>{p.published ? "published" : "draft"}</Pill>
              {p.show_in_footer && <Pill>footer</Pill>}
              <Button asChild size="icon" variant="ghost"><Link to={`/p/${p.slug}`} target="_blank" data-testid={`view-page-${p.slug}`}><ExternalLink className="h-4 w-4" /></Link></Button>
              <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setF(p); setOpen(true); }} data-testid={`edit-page-${p.slug}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => del(p)} data-testid={`delete-page-${p.slug}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </Panel>
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit page" : "New page"} onSubmit={save} testid="page-form">
        <Field label="Title"><Input required value={f.title} onChange={set("title")} data-testid="page-title-input" /></Field>
        <Field label="URL slug" hint="Leave blank to generate from the title"><Input value={f.slug} onChange={set("slug")} data-testid="page-slug-input" /></Field>
        <Field label="Content" hint="Plain text (blank line = new paragraph) or HTML (headings, links, lists, images)"><textarea className={`${inputCls} h-56 font-mono text-xs`} value={f.body} onChange={set("body")} data-testid="page-body-input" /></Field>
        <Field label="SEO title (optional)"><Input value={f.seo_title} onChange={set("seo_title")} data-testid="page-seo-title-input" /></Field>
        <Field label="Meta description (optional)"><Input value={f.meta_description} onChange={set("meta_description")} data-testid="page-meta-input" /></Field>
        <Toggle checked={f.show_in_footer} onChange={(v) => setF({ ...f, show_in_footer: v })} label="Show link in footer" testid="page-footer-toggle" />
        <Toggle checked={f.published} onChange={(v) => setF({ ...f, published: v })} label="Published" testid="page-published-toggle" />
      </FormDialog>
    </div>
  );
}
